import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QRTokenService from '../services/QRTokenService';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import DataMatrix from 'react-datamatrix-svg';
import { jsPDF } from 'jspdf';
import { toast } from 'react-toastify';
import { FaTrashAlt, FaExternalLinkAlt, FaPrint, FaPlus, FaWrench, FaBoxOpen, FaFilePdf, FaFileImage, FaFileCode, FaCopy, FaFileCsv } from 'react-icons/fa';
import styles from '../style/pages/BulkQRPage.module.css';

const BulkQRPage = () => {
    const navigate = useNavigate();
    const [expandedToken, setExpandedToken] = useState(null);
    const [tokens, setTokens] = useState([]);
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('unassigned');
    const [printSize, setPrintSize] = useState('standard');
    const [codeType, setCodeType] = useState('qr');

    useEffect(() => {
        fetchTokens();
    }, []);

    const fetchTokens = async () => {
        try {
            const data = await QRTokenService.getAllTokens();
            // Sort by createdAt desc if available, or just use natural order
            setTokens(data.sort((a,b) => b.id - a.id));
        } catch (error) {
            toast.error("Failed to fetch tokens");
        }
    };

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const newTokens = await QRTokenService.generateTokens(count);
            
            if (newTokens.length > 0 && newTokens[0].shortUrl && newTokens[0].shortUrl.includes('/scan/')) {
                toast.warning("Warning: Kutt API rejected the target URL (likely because it's a localhost or private IP). Generated using direct fallback domains.", { autoClose: 10000 });
            } else {
                toast.success(`Generated ${count} new tokens`);
            }
            
            setTokens([...newTokens, ...tokens]);
            setActiveTab('unassigned');
        } catch (error) {
            toast.error("Failed to generate tokens");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteToken = async (token) => {
        if (!window.confirm(`Are you sure you want to delete token ${token}?`)) return;
        
        try {
            await QRTokenService.deleteToken(token);
            setTokens(tokens.filter(t => t.token !== token));
            toast.info("Token deleted");
        } catch (error) {
            toast.error("Failed to delete token");
        }
    };

    const handlePurgeUnassigned = async () => {
        if (!window.confirm("Are you sure you want to delete ALL unassigned tokens? This cannot be undone.")) return;
        
        try {
            await QRTokenService.deleteUnassignedTokens();
            setTokens(tokens.filter(t => t.assigned));
            toast.info("All unassigned tokens purged");
        } catch (error) {
            toast.error("Failed to purge tokens");
        }
    };

    const handleExportCSV = () => {
        let csvContent = "qr_code,text\n";
        filteredTokens.forEach(token => {
            const qrCodeUrl = token.shortUrl || `${window.location.origin}/scan/${token.token}`;
            const safeToken = `"${token.token.replace(/"/g, '""')}"`;
            const safeUrl = `"${qrCodeUrl.replace(/"/g, '""')}"`;
            csvContent += `${safeUrl},${safeToken}\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `homebox_qr_codes_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = () => {
        // Find the grid container by searching for the class containing "qrGrid"
        const grid = document.querySelector('[class*="qrGrid"]');
        if (!grid) {
            toast.error("Grid container not found");
            return;
        }

        // Find all items by searching for class containing "qrItem"
        const items = grid.querySelectorAll('[class*="qrItem"]');
        if (items.length === 0) {
            toast.error("No items found to print");
            return;
        }

        let gridHtml = '';
        items.forEach(item => {
            const codeWrapper = item.querySelector('[class*="qrWrapper"]');
            const tokenTextElement = item.querySelector('[class*="tokenText"]');
            const tokenText = tokenTextElement?.textContent || '';
            
            if (codeWrapper) {
                // Get the SVG or content
                // Clone the wrapper and remove action buttons for a clean print
                const cleanWrapper = codeWrapper.cloneNode(true);
                const actions = cleanWrapper.querySelector('[class*="qrActions"]');
                if (actions) actions.remove();
                
                let codeHtml = cleanWrapper.innerHTML;
                
                // Ensure SVG namespace if it's an SVG
                if (codeHtml.includes('<svg') && !codeHtml.includes('xmlns=')) {
                    codeHtml = codeHtml.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
                }

                gridHtml += `
                    <div class="print-item">
                        <div class="code-box">${codeHtml}</div>
                        <div class="token-text">${tokenText}</div>
                        <div class="brand">HOMEBOX</div>
                    </div>
                `;
            }
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error("Pop-up blocked. Please allow pop-ups for this site.");
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>HomeBox - Bulk Print</title>
                    <style>
                        @page { size: A4; margin: 15mm; }
                        body { 
                            font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
                            background: white;
                            padding: 0; 
                            margin: 0; 
                        }
                        .grid-container {
                            display: grid;
                            grid-template-columns: repeat(4, 1fr);
                            gap: 12mm 10mm;
                            width: 100%;
                        }
                        [class*="qrActions"], .no-print { display: none !important; }
                        .print-item {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            padding: 5mm;
                            border: 1px solid #f0f0f0;
                            border-radius: 8px;
                            page-break-inside: avoid;
                            min-height: 50mm;
                        }
                        .code-box { 
                            width: 38mm; 
                            height: 38mm; 
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            overflow: hidden;
                        }
                        .code-box svg { 
                            width: 100% !important; 
                            height: 100% !important; 
                        }
                        .token-text { 
                            font-size: 10pt; 
                            font-weight: bold; 
                            color: #000; 
                            margin-top: 4mm;
                            font-family: monospace;
                        }
                        .brand { 
                            font-size: 7pt; 
                            letter-spacing: 2px; 
                            color: #999; 
                            margin-top: 1mm; 
                            font-weight: bold; 
                            text-transform: uppercase; 
                        }
                    </style>
                </head>
                <body>
                    <div class="grid-container">
                        ${gridHtml}
                    </div>
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                window.close();
                            }, 800);
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleDownload = async (e, tokenItem, format) => {
        const qrItem = e.currentTarget.closest('[class*="qrItem"]');
        if (!qrItem) return;

        const codeWrapper = qrItem.querySelector('[class*="qrWrapper"]');
        if (!codeWrapper) {
            toast.error("Could not find code content for download");
            return;
        }

        const svg = codeWrapper.querySelector('svg');
        if (!svg) {
            toast.error("Format conversion failed: SVG not found");
            return;
        }

        // Prepare data
        const text = tokenItem.token;
        const filename = `HB-${text}.${format}`;

        if (format === 'svg') {
            // Create a composite SVG that includes the code + text
            const serializer = new XMLSerializer();
            const originalSvgStr = serializer.serializeToString(svg);
            
            // Define dimensions for the composite SVG
            const width = 400;
            const height = 150;
            const codeSize = 130;
            
            const compositeSvg = `
                <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
                    <rect width="${width}" height="${height}" fill="white" />
                    <g transform="translate(10, 10)">
                        ${originalSvgStr.replace(/width="[^"]*"/, `width="${codeSize}"`).replace(/height="[^"]*"/, `height="${codeSize}"`)}
                    </g>
                    <g transform="translate(${codeSize + 30}, 60)">
                        <text font-family="monospace" font-size="28" font-weight="900" fill="black">${tokenItem.token}</text>
                        <text transform="translate(0, 30)" font-family="sans-serif" font-size="14" font-weight="bold" fill="#333" letter-spacing="1">HOMEBOX</text>
                    </g>
                </svg>
            `;

            const svgBlob = new Blob([compositeSvg], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } else if (format === 'jpeg' || format === 'pdf') {
            const canvas = document.createElement('canvas');
            const svgData = new XMLSerializer().serializeToString(svg);
            const img = new Image();
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            img.onload = () => {
                canvas.width = 1200; // High res
                canvas.height = 450;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                // Draw SVG (Code)
                ctx.drawImage(img, 30, 30, 390, 390);

                // Draw Text
                ctx.fillStyle = 'black';
                ctx.font = '900 84px monospace';
                ctx.fillText(tokenItem.token, 480, 210);
                
                ctx.fillStyle = '#333';
                ctx.font = 'bold 42px sans-serif';
                ctx.fillText("HOMEBOX", 480, 290);

                if (format === 'jpeg') {
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = filename;
                    link.click();
                } else if (format === 'pdf') {
                    const pdf = new jsPDF({
                        orientation: 'landscape',
                        unit: 'mm',
                        format: [120, 45]
                    });
                    const imgData = canvas.toDataURL('image/jpeg', 1.0);
                    pdf.addImage(imgData, 'JPEG', 0, 0, 120, 45);
                    pdf.save(filename);
                }
                URL.revokeObjectURL(url);
            };
            img.src = url;
        }
    };

    const handlePrintSingle = (e, tokenItem) => {
        const qrItem = e.currentTarget.closest('[class*="qrItem"]');
        if (!qrItem) return;

        // Robustly find the code wrapper while ignoring the UI icons
        const codeWrapper = qrItem.querySelector('[class*="qrWrapper"]');
        if (!codeWrapper) {
            toast.error("Could not find the code content for printing.");
            return;
        }

        // Clone and strip UI elements for a clean label print
        const cleanWrapper = codeWrapper.cloneNode(true);
        const actions = cleanWrapper.querySelector('[class*="qrActions"]');
        if (actions) actions.remove();
        
        let svgHtml = cleanWrapper.innerHTML;
        // Ensure XML namespace is present so the browser parses it as a true SVG
        if (svgHtml.includes('<svg') && !svgHtml.includes('xmlns=')) {
            svgHtml = svgHtml.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
        }

        let qrBoxHeight = '150px';
        let fontSize = '24px';
        let brandSize = '14px';
        let pageSize = 'auto';

        if (printSize === '12mm') {
            qrBoxHeight = '8mm';
            fontSize = '9px';
            brandSize = '6px';
            pageSize = '40mm 12mm'; // Landscape label
        } else if (printSize === '24mm') {
            qrBoxHeight = '14mm';
            fontSize = '12px';
            brandSize = '8px';
            pageSize = '60mm 24mm'; // Landscape label
        }

        const [width, height] = pageSize.split(' ');

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Print Label - ${tokenItem.token}</title>
                    <style>
                        @page { 
                            size: ${width} ${height}; 
                            margin: 0; 
                        }
                        * { box-sizing: border-box; -webkit-print-color-adjust: exact; }
                        html, body { 
                            margin: 0; 
                            padding: 0; 
                            width: ${width}; 
                            height: ${height};
                            overflow: hidden;
                        }
                        .label-container {
                            width: ${width};
                            height: ${height};
                            display: flex;
                            align-items: center;
                            padding: 1mm 2mm;
                            background: white;
                        }
                        .qr-box { 
                            width: ${qrBoxHeight}; 
                            height: ${qrBoxHeight}; 
                            flex-shrink: 0; 
                            display: flex; 
                            align-items: center; 
                            justify-content: center; 
                        }
                        .qr-box svg { 
                            width: 100% !important; 
                            height: 100% !important; 
                        }
                        .text-content { 
                            padding-left: 2mm;
                            display: flex; 
                            flex-direction: column; 
                            justify-content: center;
                            overflow: hidden;
                        }
                        .text { 
                            font-size: ${fontSize}; 
                            font-weight: 900; 
                            color: #000; 
                            white-space: nowrap; 
                            line-height: 1;
                            font-family: monospace;
                        }
                        .brand { 
                            margin-top: 1px;
                            font-size: ${brandSize}; 
                            color: #333; 
                            font-weight: 900; 
                            text-transform: uppercase; 
                            letter-spacing: 0.5px; 
                        }
                    </style>
                </head>
                <body onload="setTimeout(function(){ window.print(); window.close(); }, 800);">
                    <div class="label-container">
                        <div class="qr-box">${svgHtml}</div>
                        <div class="text-content">
                            <div class="text">${tokenItem.token}</div>
                            <div class="brand">HOMEBOX</div>
                        </div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const filteredTokens = tokens.filter(t => activeTab === 'assigned' ? t.assigned : !t.assigned).sort((a, b) => {
        if (!a.assigned && !b.assigned) {
            if (a.lastScannedAt && !b.lastScannedAt) return -1;
            if (!a.lastScannedAt && b.lastScannedAt) return 1;
            if (a.lastScannedAt && b.lastScannedAt) return new Date(b.lastScannedAt) - new Date(a.lastScannedAt);
        }
        return b.id - a.id;
    });

    const handleQRClick = async (token) => {
        if (!token.assigned) {
            try {
                await QRTokenService.markAsScanned(token.token);
            } catch (e) {
                console.error("Failed to mark clicked token as scanned:", e);
            }
            navigate({ search: `?page=scan_registration&qrToken=${token.token}` });
        } else {
             if (token.assetId) {
                 navigate(`/public/assets/${token.token}`);
             } else if (token.locationId) {
                 navigate(`/public/locations/${token.token}`);
             }
        }
    };

    const handleCopyLink = (e, token) => {
        e.stopPropagation();
        const link = token.shortUrl || `${window.location.origin}/scan/${token.token}`;
        navigator.clipboard.writeText(link)
            .then(() => toast.success("Link copied to clipboard!"))
            .catch(() => toast.error("Failed to copy link"));
    };

    const toggleActions = (e, tokenStr) => {
        e.stopPropagation();
        setExpandedToken(expandedToken === tokenStr ? null : tokenStr);
    };

    return (
        <div className={styles.container}>
            <div className={`${styles.header} no-print`}>
                <h1><FaBoxOpen style={{ marginRight: '1rem', verticalAlign: 'middle', opacity: 0.8 }} /> Bulk QR Management</h1>
                <p>Generate and manage unique alphanumeric tokens for high-fidelity physical labeling.</p>
            </div>

            <div className={`${styles.actionGrid} no-print`}>
                {/* Generation Card */}
                <div className={styles.actionCard}>
                    <h3><FaPlus style={{ color: 'var(--primary-color)' }} /> Generation</h3>
                    <p className={styles.cardDesc}>Create unique alphanumeric tokens for physical labels. These can be linked to assets during scanning.</p>
                    <div className={styles.cardContent}>
                        <div className={styles.inputGroup} style={{ maxWidth: '120px' }}>
                            <label>Tokens to generate</label>
                            <input 
                                type="number" 
                                value={count} 
                                onChange={(e) => setCount(e.target.value)} 
                                min="1" 
                                max="50"
                            />
                        </div>
                        <button className={styles.primaryBtn} onClick={handleGenerate} disabled={loading}>
                            {loading ? "..." : "Generate Tokens"}
                        </button>
                    </div>
                </div>

                {/* Maintenance Card */}
                <div className={styles.actionCard}>
                    <h3><FaWrench style={{ color: '#EF4444' }} /> Maintenance</h3>
                    <p className={styles.cardDesc}>Maintain your token database. Purge old unassigned tokens to keep the management grid clean.</p>
                    <div className={styles.cardContent}>
                        <button className={styles.dangerBtn} onClick={handlePurgeUnassigned}>
                            <FaTrashAlt /> Purge Unassigned
                        </button>
                    </div>
                </div>
            </div>

            <div className={`${styles.tabsContainer} no-print`}>
                <div className={styles.tabs}>
                    <div 
                        className={`${styles.tab} ${activeTab === 'unassigned' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('unassigned')}
                    >
                        Unassigned ({tokens.filter(t => !t.assigned).length})
                    </div>
                    <div 
                        className={`${styles.tab} ${activeTab === 'assigned' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('assigned')}
                    >
                        Assigned ({tokens.filter(t => t.assigned).length})
                    </div>
                </div>

                <div className={styles.viewSettings}>
                    <div className={styles.inputGroupCompact}>
                        <label>Format:</label>
                        <select value={codeType} onChange={(e) => setCodeType(e.target.value)}>
                            <option value="qr">QR Code</option>
                            <option value="barcode">Barcode</option>
                            <option value="datamatrix">Data Matrix</option>
                        </select>
                    </div>
                    <div className={styles.inputGroupCompact}>
                        <label>Size:</label>
                        <select value={printSize} onChange={(e) => setPrintSize(e.target.value)}>
                            <option value="standard">Standard</option>
                            <option value="12mm">12mm Tape</option>
                            <option value="24mm">24mm Tape</option>
                        </select>
                    </div>
                    <button 
                        className={styles.secondaryBtnCompact} 
                        onClick={handleExportCSV} 
                        disabled={filteredTokens.length === 0}
                    >
                        <FaFileCsv /> Export CSV
                    </button>
                    <button 
                        className={styles.secondaryBtnCompact} 
                        onClick={handlePrint} 
                        disabled={activeTab === 'assigned' || filteredTokens.length === 0}
                    >
                        <FaPrint /> Print Grid
                    </button>
                </div>
            </div>

            <div className={styles.qrGrid}>
                {filteredTokens.map((token) => (
                    <div 
                        key={token.id} 
                        className={`${styles.qrItem} ${token.assigned ? styles.assignedItem : ''}`}
                    >
                        <div 
                            className={styles.qrWrapper}
                            onClick={() => handleQRClick(token)}
                            style={{ cursor: 'pointer' }}
                        >
                            {codeType === 'qr' ? (
                                <QRCode 
                                    value={token.shortUrl || `${window.location.origin}/scan/${token.token}`} 
                                    size={150}
                                    level="H"
                                />
                            ) : codeType === 'barcode' ? (
                                <Barcode 
                                    value={token.token} 
                                    width={1.6}
                                    height={60}
                                    displayValue={false}
                                    renderer="svg"
                                />
                            ) : (
                                <DataMatrix 
                                    msg={token.token} 
                                    dim={150}
                                />
                            )}
                            <div 
                                className={`${styles.qrActions} no-print ${expandedToken === token.token ? styles.qrActionsExpanded : ''}`}
                                onClick={(e) => { e.stopPropagation(); toggleActions(e, null); }}
                            >
                                <button 
                                    className={styles.deleteBtn}
                                    title="Purge token"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteToken(token.token); }}
                                >
                                    <FaTrashAlt />
                                </button>
                                <div className={styles.leftGroup}>
                                    <button 
                                        className={styles.copyBtn}
                                        title="Copy link"
                                        onClick={(e) => handleCopyLink(e, token)}
                                    >
                                        <FaCopy />
                                    </button>
                                    <button 
                                        className={styles.printSingleBtn}
                                        title="Print label"
                                        onClick={(e) => { e.stopPropagation(); handlePrintSingle(e, token); }}
                                    >
                                        <FaPrint />
                                    </button>
                                </div>
                                <div className={styles.downloadGroup}>
                                    <button 
                                        className={styles.downloadBtn}
                                        title="Download SVG"
                                        onClick={(e) => { e.stopPropagation(); handleDownload(e, token, 'svg'); }}
                                    >
                                        <FaFileCode />
                                    </button>
                                    <button 
                                        className={styles.downloadBtn}
                                        title="Download JPEG"
                                        onClick={(e) => { e.stopPropagation(); handleDownload(e, token, 'jpeg'); }}
                                    >
                                        <FaFileImage />
                                    </button>
                                    <button 
                                        className={styles.downloadBtn}
                                        title="Download PDF"
                                        onClick={(e) => { e.stopPropagation(); handleDownload(e, token, 'pdf'); }}
                                    >
                                        <FaFilePdf />
                                    </button>
                                </div>
                            </div>
                            <button 
                                className={`${styles.mobileExpandBtn} no-print`}
                                onClick={(e) => toggleActions(e, token.token)}
                                title="Options"
                            >
                                <FaPlus style={{ transform: expandedToken === token.token ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }} />
                            </button>
                        </div>
                        <div className={styles.tokenText}>{token.token}</div>
                        
                        {token.assigned ? (
                            <div className={styles.assignedBadge}>
                                {token.assetId ? `ASSET #${token.assetId}` : `LOC #${token.locationId}`}
                                <FaExternalLinkAlt style={{ marginLeft: '4px', fontSize: '0.5rem' }} />
                            </div>
                        ) : token.lastScannedAt ? (
                            <div style={{backgroundColor: '#eab308', color: '#fff', fontSize: '0.65rem', padding: '3px 6px', borderRadius: '4px', fontWeight: 'bold', marginTop: '2px', letterSpacing: '0.5px'}}>RECENTLY SCANNED</div>
                        ) : (
                            <div className={styles.branding}>HOMEBOX</div>
                        )}
                    </div>
                ))}
            </div>

            {filteredTokens.length === 0 && (
                <div className={`${styles.emptyState} no-print`}>
                    {activeTab === 'assigned' 
                        ? "No assigned tokens found. Start scanning new QR codes to link them!"
                        : "No unassigned tokens available. Generate some above!"}
                </div>
            )}
        </div>
    );
};

export default BulkQRPage;
